import type { ComponentPropsWithRef, FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import { CrossIcon } from '../Icons/Navigational/Cross'
import { Link } from '../Link'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './TagClickable.module.scss'

export const TagClickable: FunctionComponent<Props> = ({
  text,
  href,
  withIcon,
  clickHandler,
  className,
  ...rest
}) => {
  if (!text) {
    return null
  }

  const TagButton = () => (
    <button
      className={classnames(
        withIcon ? styles.clickableTagWithIcon : styles.clickableTag,
        className
      )}
      data-testid={`${
        withIcon
          ? 'clickable-tag-with-icon-container'
          : 'clickable-tag-container'
      }`}
      tabIndex={0}
      onClick={clickHandler}
      {...rest}
    >
      {withIcon ? (
        <>
          <TypographyV2
            textStyle="sb-text-interface-body-x-small-regular"
            tag="span"
            className={styles.text}
          >
            {text}
          </TypographyV2>
          <span className={styles.iconWrapper}>
            <CrossIcon className={styles.icon} />
          </span>
        </>
      ) : (
        <TypographyV2
          textStyle="sb-text-interface-body-x-small-regular"
          className="sb-link-animation-wrapper"
          tag="span"
        >
          {text}
        </TypographyV2>
      )}
    </button>
  )

  return withIcon ? (
    <TagButton />
  ) : href ? (
    <Link
      href={href}
      className={classnames(styles.clickableTag, className)}
      data-testid="clickable-tag-container"
      appearance="secondary"
      textStyle="sb-text-interface-body-x-small-regular"
      {...rest}
    >
      {text}
    </Link>
  ) : (
    <TagButton />
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Text displayed inside of the ClickableTag component.
   */
  text: string
  /**
   * String to be used as the href attribute for the Link component. If present, a Link will be rendered instead of button, and the click handler will not be used. Ignored if withIcon is true.
   */
  href?: string
  /**
   * Optional boolean to allow the cross icon to be rendered if true. If set to true, a button will always be rendered with the clickHandler applied, and the href prop will be ignored.
   */
  withIcon?: boolean
  /**
   * Function to be passed to on click of ClickableTag component. Only used if rendering a button (i.e. when href prop is not provided and/or withIcon is true).
   */
  clickHandler?: () => void
  /**
   * Optional CSS class name that can be added to the ClickableTag component.
   */
  className?: string
} & ComponentPropsWithRef<'button'> &
  HTMLProps<HTMLAnchorElement>
